/**
 * Compatibility surface derived from the rudder-cli output in ./analytics-events.ts.
 * Do not edit the event registry or custom-type aliases by hand; run pnpm analytics:generate.
 */
import type {
  CustomTypeActivationChatOpenSource,
  CustomTypeActivationLinkModel,
  CustomTypeActivationReadOutcome,
  CustomTypeActivationReadRefusal,
  CustomTypeAgentErrorCode,
  CustomTypeAiAttachmentType,
  CustomTypeAiOutcome,
  CustomTypeAiSource,
  CustomTypeAiSurface,
  CustomTypeAnalyticsEntryPoint,
  CustomTypeAssistCardKind,
  CustomTypeAssistCardSource,
  CustomTypeAudiencePreviewSurface,
  CustomTypeAuthProvider,
  CustomTypeChatDiscussionTopic,
  CustomTypeChatSuggestionSource,
  CustomTypeChatSuggestionSurface,
  CustomTypeChatWelcomeCategory,
  CustomTypeContextEntryCreatedEntryType,
  CustomTypeContextEntryDeletedEntryType,
  CustomTypeContextEntrySensitiveDataBlockedEntryType,
  CustomTypeContextEntryUpdatedEntryType,
  CustomTypeContextReviewSuggestionAppliedOrigin,
  CustomTypeContextReviewSuggestionDismissedOrigin,
  CustomTypeDashboardOpenSource,
  CustomTypeExternalMcpToolCalledScope,
  CustomTypeMcpCredential,
  CustomTypeMcpTokenCreatedScope,
  CustomTypeMcpTokenRevokedScope,
  CustomTypeMemorySource,
  CustomTypeRsAccountOauthAbandonedReason,
  CustomTypeScmProvider,
  CustomTypeScope,
  CustomTypeSensitiveScanField,
  CustomTypeSignInEntryPoint,
  CustomTypeSignInFailureReason,
  CustomTypeSlackSurface,
  CustomTypeSlackUserUnlinkedScope,
  CustomTypeUserSignedInMethod,
  CustomTypeUserSignedUpMethod,
  CustomTypeUserSignInStartedMethod,
  CustomTypeWarehouseConnectSurface,
  CustomTypeWarehouseErrorKind,
  CustomTypeWebhookIgnoredReason,
  CustomTypeWorkflowErrorKind,
  CustomTypeWorkspaceJoinedMethod,
  CustomTypeWorkspaceSource,
  RudderTyper,
} from './analytics-events.js';

export type ActivationChatOpenSource = CustomTypeActivationChatOpenSource;
export type ActivationLinkModel = CustomTypeActivationLinkModel;
export type ActivationReadOutcome = CustomTypeActivationReadOutcome;
export type ActivationReadRefusal = CustomTypeActivationReadRefusal;
export type AgentErrorCode = CustomTypeAgentErrorCode;
export type AiAttachmentType = CustomTypeAiAttachmentType;
export type AiOutcome = CustomTypeAiOutcome;
export type AiSource = CustomTypeAiSource;
export type AiSurface = CustomTypeAiSurface;
export type AnalyticsEntryPoint = CustomTypeAnalyticsEntryPoint;
export type AssistCardKind = CustomTypeAssistCardKind;
export type AssistCardSource = CustomTypeAssistCardSource;
export type AudiencePreviewSurface = CustomTypeAudiencePreviewSurface;
export type AuthProvider = CustomTypeAuthProvider;
export type ChatDiscussionTopic = CustomTypeChatDiscussionTopic;
export type ChatSuggestionSource = CustomTypeChatSuggestionSource;
export type ChatSuggestionSurface = CustomTypeChatSuggestionSurface;
export type ChatWelcomeCategory = CustomTypeChatWelcomeCategory;
export type ContextEntryCreatedEntryType = CustomTypeContextEntryCreatedEntryType;
export type ContextEntryDeletedEntryType = CustomTypeContextEntryDeletedEntryType;
export type ContextEntrySensitiveDataBlockedEntryType = CustomTypeContextEntrySensitiveDataBlockedEntryType;
export type ContextEntryUpdatedEntryType = CustomTypeContextEntryUpdatedEntryType;
export type ContextReviewSuggestionAppliedOrigin = CustomTypeContextReviewSuggestionAppliedOrigin;
export type ContextReviewSuggestionDismissedOrigin = CustomTypeContextReviewSuggestionDismissedOrigin;
export type DashboardOpenSource = CustomTypeDashboardOpenSource;
export type ExternalMcpToolCalledScope = CustomTypeExternalMcpToolCalledScope;
export type McpCredential = CustomTypeMcpCredential;
export type McpTokenCreatedScope = CustomTypeMcpTokenCreatedScope;
export type McpTokenRevokedScope = CustomTypeMcpTokenRevokedScope;
export type MemorySource = CustomTypeMemorySource;
export type RsAccountOauthAbandonedReason = CustomTypeRsAccountOauthAbandonedReason;
export type ScmProvider = CustomTypeScmProvider;
export type Scope = CustomTypeScope;
export type SensitiveScanField = CustomTypeSensitiveScanField;
export type SignInEntryPoint = CustomTypeSignInEntryPoint;
export type SignInFailureReason = CustomTypeSignInFailureReason;
export type SlackSurface = CustomTypeSlackSurface;
export type SlackUserUnlinkedScope = CustomTypeSlackUserUnlinkedScope;
export type UserSignInStartedMethod = CustomTypeUserSignInStartedMethod;
export type UserSignedInMethod = CustomTypeUserSignedInMethod;
export type UserSignedUpMethod = CustomTypeUserSignedUpMethod;
export type WarehouseConnectSurface = CustomTypeWarehouseConnectSurface;
export type WarehouseErrorKind = CustomTypeWarehouseErrorKind;
export type WebhookIgnoredReason = CustomTypeWebhookIgnoredReason;
export type WorkflowErrorKind = CustomTypeWorkflowErrorKind;
export type WorkspaceJoinedMethod = CustomTypeWorkspaceJoinedMethod;
export type WorkspaceSource = CustomTypeWorkspaceSource;

type ExternalTrackMethod = 'trackAiResponseReceived' | 'trackAiUserPromptCreated';
type TrackMethodName = Exclude<Extract<keyof RudderTyper, `track${string}`>, ExternalTrackMethod>;
type EventNameFromMethod<T extends TrackMethodName> = T extends `track${infer Name}` ? Uncapitalize<Name> : never;
type MethodForEvent<T extends AnalyticsEventName> = `track${Capitalize<T>}` & TrackMethodName;
type FirstArg<T extends TrackMethodName> = Parameters<RudderTyper[T]>[0];
type WirePropertyName<T extends PropertyKey> = T extends 'type_' ? 'type' : T;
type WireProperties<T> = { [Key in keyof T as WirePropertyName<Key>]: T[Key] };
// rudder-cli emits `options?: ApiOptions` as the first parameter only for events with no properties.
// It does not currently emit `props?: Foo`; if it does, this conditional needs to distinguish props from options.
type PropertiesForEvent<T extends AnalyticsEventName> =
  undefined extends FirstArg<MethodForEvent<T>> ? Record<string, never> : WireProperties<FirstArg<MethodForEvent<T>>>;

export type AnalyticsEventName = EventNameFromMethod<TrackMethodName>;
export type AnalyticsEventProperties = { [Event in AnalyticsEventName]: PropertiesForEvent<Event> };

type AnalyticsEventRegistry = { readonly [Event in AnalyticsEventName]: Event };

export const ANALYTICS_EVENTS = Object.freeze({
  activationAudienceNameResolved: 'activationAudienceNameResolved',
  activationChatAudienceLinkClicked: 'activationChatAudienceLinkClicked',
  activationChatClosed: 'activationChatClosed',
  activationChatOpened: 'activationChatOpened',
  activationFailedRecordsRead: 'activationFailedRecordsRead',
  activationSyncRunStarted: 'activationSyncRunStarted',
  activationSyncRunStopped: 'activationSyncRunStopped',
  agentTurnCompleted: 'agentTurnCompleted',
  assistCardActed: 'assistCardActed',
  assistCardCompleted: 'assistCardCompleted',
  assistCardDismissed: 'assistCardDismissed',
  assistCardShown: 'assistCardShown',
  audienceActivationCreated: 'audienceActivationCreated',
  audienceActivationDeleted: 'audienceActivationDeleted',
  audienceActivationUpdated: 'audienceActivationUpdated',
  audienceCounted: 'audienceCounted',
  audienceCreated: 'audienceCreated',
  audienceDeleted: 'audienceDeleted',
  audiencePredicateRawSqlViewed: 'audiencePredicateRawSqlViewed',
  audiencePreviewed: 'audiencePreviewed',
  audienceProbeUsed: 'audienceProbeUsed',
  audienceRenamed: 'audienceRenamed',
  audienceRsSourceCreated: 'audienceRsSourceCreated',
  audienceRsSourceDeleted: 'audienceRsSourceDeleted',
  audienceRsSourceUpdated: 'audienceRsSourceUpdated',
  audienceSqlViewed: 'audienceSqlViewed',
  audienceUpdated: 'audienceUpdated',
  chatBudgetExhausted: 'chatBudgetExhausted',
  chatDeleted: 'chatDeleted',
  chatFeedbackChanged: 'chatFeedbackChanged',
  chatInteractiveAnswered: 'chatInteractiveAnswered',
  chatMessageSent: 'chatMessageSent',
  chatRenamed: 'chatRenamed',
  chatSuggestionClicked: 'chatSuggestionClicked',
  chatSuggestionSent: 'chatSuggestionSent',
  chatSuggestionShown: 'chatSuggestionShown',
  chatThreadOpened: 'chatThreadOpened',
  chatTurnStopped: 'chatTurnStopped',
  chatTurnSummarized: 'chatTurnSummarized',
  chatVisibilityChanged: 'chatVisibilityChanged',
  clientErrorRaised: 'clientErrorRaised',
  contextCleanupScanCompleted: 'contextCleanupScanCompleted',
  contextCleanupScanFailed: 'contextCleanupScanFailed',
  contextCleanupScanStarted: 'contextCleanupScanStarted',
  contextEntryCreated: 'contextEntryCreated',
  contextEntryDeleted: 'contextEntryDeleted',
  contextEntrySensitiveDataBlocked: 'contextEntrySensitiveDataBlocked',
  contextEntryUpdated: 'contextEntryUpdated',
  contextMemoryProposalApproved: 'contextMemoryProposalApproved',
  contextMemoryProposalCreated: 'contextMemoryProposalCreated',
  contextMemoryProposalRejected: 'contextMemoryProposalRejected',
  contextProposalAnalysisRetried: 'contextProposalAnalysisRetried',
  contextReviewSuggestionApplied: 'contextReviewSuggestionApplied',
  contextReviewSuggestionDismissed: 'contextReviewSuggestionDismissed',
  contextSettingsChanged: 'contextSettingsChanged',
  dashboardCreated: 'dashboardCreated',
  dashboardDeleted: 'dashboardDeleted',
  dashboardPanelReferenced: 'dashboardPanelReferenced',
  dashboardPreviewed: 'dashboardPreviewed',
  dashboardPrivacyChanged: 'dashboardPrivacyChanged',
  dashboardQueried: 'dashboardQueried',
  dashboardShareTokenRegenerated: 'dashboardShareTokenRegenerated',
  dashboardStarred: 'dashboardStarred',
  dashboardUpdated: 'dashboardUpdated',
  dashboardViewed: 'dashboardViewed',
  demoWorkspacePurged: 'demoWorkspacePurged',
  docDeleted: 'docDeleted',
  externalMcpOauthCompleted: 'externalMcpOauthCompleted',
  externalMcpToolCalled: 'externalMcpToolCalled',
  githubAppInstalled: 'githubAppInstalled',
  githubAppUninstalled: 'githubAppUninstalled',
  mcpClientRegistered: 'mcpClientRegistered',
  mcpClientRevoked: 'mcpClientRevoked',
  mcpTokenCreated: 'mcpTokenCreated',
  mcpTokenRevoked: 'mcpTokenRevoked',
  pageViewed: 'pageViewed',
  publicDashboardViewed: 'publicDashboardViewed',
  rsAccountCreated: 'rsAccountCreated',
  rsAccountDeleted: 'rsAccountDeleted',
  rsAccountOauthAbandoned: 'rsAccountOauthAbandoned',
  rsAccountOauthStarted: 'rsAccountOauthStarted',
  rsAccountReauthorized: 'rsAccountReauthorized',
  rsAccountUpdated: 'rsAccountUpdated',
  rudderstackDisconnected: 'rudderstackDisconnected',
  rudderstackLinked: 'rudderstackLinked',
  skillCreated: 'skillCreated',
  skillDeleted: 'skillDeleted',
  skillInvoked: 'skillInvoked',
  skillStatusChanged: 'skillStatusChanged',
  skillUpdated: 'skillUpdated',
  slackAssistantThreadStarted: 'slackAssistantThreadStarted',
  slackChannelBound: 'slackChannelBound',
  slackChannelUnbound: 'slackChannelUnbound',
  slackInstalled: 'slackInstalled',
  slackMessageReceived: 'slackMessageReceived',
  slackSlashCommandUsed: 'slackSlashCommandUsed',
  slackUninstalled: 'slackUninstalled',
  slackUserLinked: 'slackUserLinked',
  slackUserUnlinked: 'slackUserUnlinked',
  userSignInFailed: 'userSignInFailed',
  userSignInStarted: 'userSignInStarted',
  userSignedIn: 'userSignedIn',
  userSignedOut: 'userSignedOut',
  userSignedUp: 'userSignedUp',
  warehouseConnected: 'warehouseConnected',
  warehouseConnectionUpdated: 'warehouseConnectionUpdated',
  warehouseDeleted: 'warehouseDeleted',
  warehouseDocCompileStarted: 'warehouseDocCompileStarted',
  warehouseDocRecompileStarted: 'warehouseDocRecompileStarted',
  warehouseErrorUnclassified: 'warehouseErrorUnclassified',
  warehouseIndexed: 'warehouseIndexed',
  warehouseKeypairCreated: 'warehouseKeypairCreated',
  warehouseProjectLinked: 'warehouseProjectLinked',
  warehouseProjectUnlinked: 'warehouseProjectUnlinked',
  warehousePromptResolved: 'warehousePromptResolved',
  warehouseQueryExecuted: 'warehouseQueryExecuted',
  warehouseRefreshed: 'warehouseRefreshed',
  warehouseSynced: 'warehouseSynced',
  warehouseTested: 'warehouseTested',
  webhookDispatched: 'webhookDispatched',
  webhookIgnored: 'webhookIgnored',
  webhookReceived: 'webhookReceived',
  workflowActivated: 'workflowActivated',
  workflowCreated: 'workflowCreated',
  workflowDeleted: 'workflowDeleted',
  workflowDeliveryFailed: 'workflowDeliveryFailed',
  workflowDraftDiscarded: 'workflowDraftDiscarded',
  workflowDraftSaved: 'workflowDraftSaved',
  workflowDryRunExecuted: 'workflowDryRunExecuted',
  workflowPaused: 'workflowPaused',
  workflowRenamed: 'workflowRenamed',
  workflowRunCompleted: 'workflowRunCompleted',
  workflowRunFailed: 'workflowRunFailed',
  workflowRunStarted: 'workflowRunStarted',
  workflowSchedulerTick: 'workflowSchedulerTick',
  workspaceCreated: 'workspaceCreated',
  workspaceDeleted: 'workspaceDeleted',
  workspaceInviteResent: 'workspaceInviteResent',
  workspaceInviteRevoked: 'workspaceInviteRevoked',
  workspaceInviteViewed: 'workspaceInviteViewed',
  workspaceJoined: 'workspaceJoined',
  workspaceMemberInvited: 'workspaceMemberInvited',
  workspaceMemberLeft: 'workspaceMemberLeft',
  workspaceMemberRemoved: 'workspaceMemberRemoved',
  workspaceMemberRoleChanged: 'workspaceMemberRoleChanged',
} as const satisfies AnalyticsEventRegistry);

type _AllKeys<U> = U extends unknown ? keyof U : never;
type _Same<X, Y> = [X] extends [Y] ? ([Y] extends [X] ? true : false) : false;
type _SameProperties<T extends AnalyticsEventName, All extends AnalyticsEventName = T> = T extends unknown
  ? _Same<AnalyticsEventProperties[T], AnalyticsEventProperties[All]> extends true
    ? _Same<keyof AnalyticsEventProperties[T], _AllKeys<AnalyticsEventProperties[All]>>
    : false
  : never;

/**
 * Intersect a payload with this. A single event is unaffected. A union of events is accepted only
 * when every member declares the same keys with mutually assignable types. Otherwise `properties`
 * widens to the union and accepts a key declared on any member.
 */
export type SingleShapeEvent<T extends AnalyticsEventName> = [_SameProperties<T>] extends [true]
  ? unknown
  : { readonly __eventsInThisUnionDeclareDifferentProperties: never };

type _MissingAnalyticsProperties = Exclude<AnalyticsEventName, keyof AnalyticsEventProperties>;
type _ExtraAnalyticsProperties = Exclude<keyof AnalyticsEventProperties, AnalyticsEventName>;
/** Compile-time guard: every generated track method must have a matching properties type. */
export type AssertAnalyticsPropertiesComplete = _MissingAnalyticsProperties extends never
  ? true
  : `Missing AnalyticsEventProperties for: ${_MissingAnalyticsProperties}`;
/** Compile-time guard: no orphan property types without a matching event name. */
export type AssertAnalyticsPropertiesNoExtra = _ExtraAnalyticsProperties extends never
  ? true
  : `Extra AnalyticsEventProperties for: ${_ExtraAnalyticsProperties}`;
export type AssertAnalyticsRegistryComplete = AssertAnalyticsPropertiesComplete;

/** Definition-site exhaustiveness check — fails typecheck if registry and properties drift. */
export const ANALYTICS_REGISTRY_COMPLETE: AssertAnalyticsRegistryComplete = true;
